import React from 'react';
import { gql, useQuery } from '@apollo/client';
import Card from '../../../../ui/canon/Card';
import Button from '../../../../ui/canon/Button';
import { formatCurrency } from '../../utils/formatters';

// Compact, theme-aware wealth gap visualization
// Props:
// - currentValue: number (current investable assets)
// - targetValue: number (target wealth goal)
// - showTooltip?: boolean
// Pull latest wealth_gap TriggeringEvent if values not provided
const GET_WEALTH_GAP_EVENT = gql`
  query GetWealthGapEventMini {
    getCurrentUser {
      id
      triggering_events(limit: 1, event_type: "wealth_gap") {
        event_type
        completed_at
        payload
      }
    }
  }
`;

const WealthGapMiniCard = ({ currentValue, targetValue, showTooltip = true }) => {
  const { data } = useQuery(GET_WEALTH_GAP_EVENT, { fetchPolicy: 'cache-first' });
  const latest = data?.getCurrentUser?.triggering_events?.[0]?.payload || {};
  const currentFromEvt = Number(latest.current_assets || 0);
  const targetFromEvt = Number(latest.goal_lump_sum || 0);

  const current = Math.max(0, Number((currentValue != null ? currentValue : currentFromEvt) || 0));
  const target = Math.max(0, Number((targetValue != null ? targetValue : targetFromEvt) || 0));
  const gap = Math.max(0, target - current);

  const pct = target > 0 ? Math.min(1, current / target) : 0;
  const overPct = target > 0 && current > target ? Math.min(0.35, (current - target) / target) : 0; // cap overflow display

  const currency = (n) => formatCurrency(Number(n || 0));

  const hasData = (current > 0) || (target > 0) || (gap > 0);

  if (!hasData) {
    return (
      <Card className="h-full overflow-hidden" data-wealth-gap-card="true">
        <div className="px-4 py-5 sm:p-6 h-full flex flex-col items-center justify-center text-center">
          <h3 className="mb-1">Wealth Gap</h3>
          <p className="text-succession-text-secondary text-sm max-w-xs mb-3">Do you know how much you need to retire? Quantify your wealth goal and see the gap instantly.</p>
          <Button type="button" onClick={() => window.location.href = '/succession/plans'}>
            Start Succession Plan
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="h-full overflow-hidden" data-wealth-gap-card="true">
      <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
        <h3 className="mb-1">Wealth Gap</h3>
        <h4 className="text-sm text-succession-text-secondary">What you’ll need at exit versus what you have.</h4>
        {/* Key figures */}
        <div className="grid grid-cols-2 gap-6 mt-3 text-center">
          <div>
            {gap > 0 && <style>{"@keyframes valuePulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.04); opacity: 0.9; } }"}</style>}
            <h3
              className={`mb-1 font-semibold ${gap > 0 ? 'text-succession-error' : 'text-succession-success'}`}
              style={gap > 0 ? { animation: 'valuePulse 1.6s ease-in-out infinite' } : undefined}
            >
              {currency(gap)}
            </h3>
            <div className="text-xs text-succession-text-secondary">Your Wealth Gap</div>
          </div>
          <div>
            <h3 className="mb-1 text-succession-text-primary font-semibold">{currency(target)}</h3>
            <div className="text-xs text-succession-text-secondary">Your Goal</div>
          </div>
        </div>

        {/* Vertical stacked bar */}
        <div className="mt-4 flex flex-col items-center justify-center flex-1">
          <div className="relative w-20 md:w-24 h-40 rounded bg-succession-bg-secondary border border-succession-border overflow-hidden" aria-label="Wealth gap progress bar vertical">
            {/* Current */}
            <div className="absolute left-0 right-0 bottom-0 bg-succession-primary" style={{ height: `${Math.max(2, pct * 100)}%` }} />
            {/* Overflow beyond goal */}
            {overPct > 0 && (
              <div className="absolute left-0 right-0 bg-succession-success" style={{ bottom: '100%', height: `${overPct * 100}%` }} />
            )}
            {/* Goal marker */}
            <div className="absolute left-0 right-0 h-0.5 bg-succession-text-muted" style={{ bottom: '100%' }} />
          </div>
          <div className="mt-3 text-xs text-succession-text-secondary">
            Current Net Investable Assets: <span className="text-succession-text-primary font-semibold" title={showTooltip ? currency(current) : undefined}>{currency(current)}</span>
          </div>
        </div>

        <div className="mt-auto pt-2 flex justify-center">
          <a href="/succession/readiness/wealth-gap" className="text-sm text-succession-primary hover:text-succession-primary-hover">Review Wealth Gap &gt;</a>
        </div>
      </div>
    </Card>
  );
};

export default WealthGapMiniCard;


